import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/auth-context";
import { usePersistedState } from "@/hooks/use-persisted-state";
import { apiClient } from "@/lib/api-client";
import { useToast } from "@/hooks/use-toast";
import { trackEvent, trackScreenView, setAnalyticsUserProperty, AnalyticsEvents } from "@/lib/analytics";
import {
  getCachedChildren,
  setCachedChildren,
  getCachedSleepEvents,
  setCachedSleepEvents,
  getCachedGoals,
  setCachedGoals,
} from "@/lib/storage";

import { Header } from "@/components/header";
import { ChildSelection } from "@/components/child-selection";
import { SleepLogDialog } from "@/components/sleep-log-dialog";
import type { SleepEvent, SleepGoal, Child, NewSleepEvent } from "@/lib/types";
import { OnboardingWelcome } from "@/components/onboarding-welcome";
import { AuthGuard } from "@/components/auth/auth-guard";
import { TabsLayout } from "@/components/tabs-layout";
import { HomeTab } from "@/components/tabs/home-tab";
import { GoalsTab } from "@/components/tabs/goals-tab";
import { AICoachTab } from "@/components/tabs/ai-coach-tab";
import { LogTab } from "@/components/tabs/log-tab";

// Free tier limits
const MAX_FREE_CHILDREN = 2;

export default function HomePage() {
  const { user, isSubscribed } = useAuth();
  const { toast } = useToast();

  const [selectedChildId, setSelectedChildId] = usePersistedState<string>('selectedChildId', '');

  // Optimistic state initialization from local cache
  const [children, setChildren] = useState<Child[]>(() => {
    if (!user?.uid) return [];
    return getCachedChildren(user.uid) || [];
  });

  const [events, setEvents] = useState<SleepEvent[]>(() => {
    if (!user?.uid || !selectedChildId) return [];
    return getCachedSleepEvents(user.uid, selectedChildId) || [];
  });
  const [goals, setGoals] = useState<SleepGoal[]>(() => {
    if (!user?.uid || !selectedChildId) return [];
    return getCachedGoals(user.uid, selectedChildId) || [];
  });

  const [activeTab, setActiveTab] = usePersistedState<string>('activeTab', 'home');
  const [isSleepLogOpen, setIsSleepLogOpen] = useState(false);

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
    }
  };

  // Reset scroll to top when changing to non-chat tabs so tabs do not start scrolled to arbitrary positions.
  // The AI Coach tab manages its own scroll position to start at the most recent messages at the bottom.
  useEffect(() => {
    if (activeTab !== 'coach') {
      scrollToTop();
    }
  }, [activeTab]);

  // Track screen view on mount
  useEffect(() => {
    trackScreenView('Home');
  }, []);

  /* Loading state: unblocks immediately if cached data is present */
  const [isLoading, setIsLoading] = useState<boolean>(() => {
    if (!user?.uid) return false;
    const cached = getCachedChildren(user.uid);
    return !cached || cached.length === 0;
  });

  // Track the child ID currently loaded to prevent redundant fetches
  const prevSelectedChildIdRef = useRef<string>(selectedChildId);

  // Initial bootstrap: single round-trip fetch with optimistic cache hydration
  useEffect(() => {
    if (!user?.uid) {
      setChildren([]);
      setSelectedChildId('');
      setEvents([]);
      setGoals([]);
      setIsLoading(false);
      return;
    }

    // Hydrate optimistic state from cache if not already populated
    const cachedChildren = getCachedChildren(user.uid);
    if (cachedChildren && cachedChildren.length > 0) {
      setChildren(cachedChildren);
      const activeChildId = selectedChildId && cachedChildren.some(c => c.id === selectedChildId)
        ? selectedChildId
        : cachedChildren[0].id;

      if (activeChildId !== selectedChildId) {
        setSelectedChildId(activeChildId);
      }

      const cachedEvents = getCachedSleepEvents(user.uid, activeChildId);
      if (cachedEvents) {
        setEvents(cachedEvents);
      }
      const cachedGoals = getCachedGoals(user.uid, activeChildId);
      if (cachedGoals) {
        setGoals(cachedGoals);
      }
      setIsLoading(false);
    }

    const fetchInitialData = async () => {
      try {
        if (typeof apiClient.getBootstrapData === 'function') {
          const response = await apiClient.getBootstrapData(selectedChildId || undefined);
          if (response.success && response.data) {
            const {
              children: childrenData,
              sleepEvents: eventsData,
              goals: goalsData,
              selectedChildId: serverSelectedChildId,
            } = response.data;

            setChildren(childrenData);
            setCachedChildren(user.uid, childrenData);
            setAnalyticsUserProperty('child_count', childrenData.length.toString());

            let activeId = serverSelectedChildId || selectedChildId;
            if (childrenData.length === 0) {
              activeId = '';
              setSelectedChildId('');
            } else if (!activeId || !childrenData.some(c => c.id === activeId)) {
              activeId = childrenData[0].id;
              setSelectedChildId(activeId);
            } else if (activeId !== selectedChildId) {
              setSelectedChildId(activeId);
            }

            prevSelectedChildIdRef.current = activeId;

            if (activeId) {
              setEvents(eventsData || []);
              setCachedSleepEvents(user.uid, activeId, eventsData || []);
              setGoals(goalsData || []);
              setCachedGoals(user.uid, activeId, goalsData || []);
            } else {
              setEvents([]);
              setGoals([]);
            }
            setIsLoading(false);
            return;
          }
        }

        // Fallback for tests or environments where getBootstrapData is unavailable
        const response = await apiClient.getChildren();
        if (response.success && response.data) {
          const childrenData = response.data;
          setChildren(childrenData);
          setCachedChildren(user.uid, childrenData);
          setAnalyticsUserProperty('child_count', childrenData.length.toString());

          let activeId = selectedChildId;
          if (childrenData.length === 0) {
            activeId = '';
            setSelectedChildId('');
          } else if (selectedChildId && !childrenData.some(c => c.id === selectedChildId)) {
            activeId = childrenData[0].id;
            setSelectedChildId(activeId);
          } else if (!selectedChildId && childrenData.length > 0) {
            activeId = childrenData[0].id;
            setSelectedChildId(activeId);
          }

          prevSelectedChildIdRef.current = activeId;

          if (activeId) {
            const [eventsResponse, goalsResponse] = await Promise.all([
              apiClient.getSleepEvents(activeId),
              apiClient.getGoals(activeId),
            ]);
            if (eventsResponse.success && eventsResponse.data) {
              setEvents(eventsResponse.data);
              setCachedSleepEvents(user.uid, activeId, eventsResponse.data);
            }
            if (goalsResponse.success && goalsResponse.data) {
              setGoals(goalsResponse.data);
              setCachedGoals(user.uid, activeId, goalsResponse.data);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [user?.uid, isSubscribed]);

  // When selected child changes after initial load, display cached data immediately and refresh in parallel
  useEffect(() => {
    if (!user?.uid || !selectedChildId) return;

    if (prevSelectedChildIdRef.current === selectedChildId) {
      return;
    }
    prevSelectedChildIdRef.current = selectedChildId;

    // Optimistically load cached data for this child
    const cachedEvents = getCachedSleepEvents(user.uid, selectedChildId);
    if (cachedEvents) {
      setEvents(cachedEvents);
    }
    const cachedGoals = getCachedGoals(user.uid, selectedChildId);
    if (cachedGoals) {
      setGoals(cachedGoals);
    }

    const fetchChildData = async () => {
      try {
        const [eventsResponse, goalsResponse] = await Promise.all([
          apiClient.getSleepEvents(selectedChildId),
          apiClient.getGoals(selectedChildId),
        ]);

        if (eventsResponse.success && eventsResponse.data) {
          setEvents(eventsResponse.data);
          setCachedSleepEvents(user.uid, selectedChildId, eventsResponse.data);
        }

        if (goalsResponse.success && goalsResponse.data) {
          setGoals(goalsResponse.data);
          setCachedGoals(user.uid, selectedChildId, goalsResponse.data);
        }
      } catch (error) {
        console.error("Error fetching child data on switch:", error);
      }
    };

    fetchChildData();
  }, [user?.uid, selectedChildId]);

  const selectedChild = children.find(c => c.id === selectedChildId);
  const selectedChildGoal = goals.find(g => g.childId === selectedChildId);

  const addSleepEvent = async (newEvent: Omit<NewSleepEvent, 'childId'>) => {
    if (!user || !selectedChildId) return;

    try {
      const response = await apiClient.createSleepEvent({
        childId: selectedChildId,
        time: newEvent.time,
        type: newEvent.type,
        duration: newEvent.duration,
        notes: newEvent.notes,
      });

      if (response.success && response.data) {
        // Track sleep event logged
        trackEvent(AnalyticsEvents.SLEEP_EVENT_LOGGED, { event_type: newEvent.type });

        // Refresh events
        const eventsResponse = await apiClient.getSleepEvents(selectedChildId);
        if (eventsResponse.success && eventsResponse.data) {
          setEvents(eventsResponse.data);
          setCachedSleepEvents(user.uid, selectedChildId, eventsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error adding sleep event:", error);
      // Enhanced error handling will be applied through the API client
    }
  };

  const addSleepEventsBatch = async (newEvents: Omit<NewSleepEvent, 'childId'>[]) => {
    if (!user || !selectedChildId || newEvents.length === 0) return;

    try {
      for (const newEvent of newEvents) {
        await apiClient.createSleepEvent({
          childId: selectedChildId,
          time: newEvent.time,
          type: newEvent.type,
          duration: newEvent.duration,
          notes: newEvent.notes,
        });
        trackEvent(AnalyticsEvents.SLEEP_EVENT_LOGGED, { event_type: newEvent.type });
      }

      // Refresh events once at the end
      const eventsResponse = await apiClient.getSleepEvents(selectedChildId);
      if (eventsResponse.success && eventsResponse.data) {
        setEvents(eventsResponse.data);
        setCachedSleepEvents(user.uid, selectedChildId, eventsResponse.data);
      }
    } catch (error) {
      console.error("Error adding batch sleep events:", error);
    }
  };

  const updateSleepEvent = async (eventId: string, updatedEvent: Omit<NewSleepEvent, 'childId'>) => {
    if (!user) return;

    try {
      const response = await apiClient.updateSleepEvent(eventId, updatedEvent);
      if (response.success && response.data) {
        // Track sleep event edited
        trackEvent(AnalyticsEvents.SLEEP_EVENT_EDITED, { event_type: updatedEvent.type });

        // Refresh events
        const eventsResponse = await apiClient.getSleepEvents(selectedChildId);
        if (eventsResponse.success && eventsResponse.data) {
          setEvents(eventsResponse.data);
          setCachedSleepEvents(user.uid, selectedChildId, eventsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error updating sleep event:", error);
    }
  };

  const deleteSleepEvent = async (eventId: string) => {
    if (!user) return;

    try {
      const response = await apiClient.deleteSleepEvent(eventId);
      if (response.success) {
        // Track sleep event deleted
        trackEvent(AnalyticsEvents.SLEEP_EVENT_DELETED);

        // Refresh events
        const eventsResponse = await apiClient.getSleepEvents(selectedChildId);
        if (eventsResponse.success && eventsResponse.data) {
          setEvents(eventsResponse.data);
          setCachedSleepEvents(user.uid, selectedChildId, eventsResponse.data);
        }
      }
    } catch (error) {
      console.error("Error deleting sleep event:", error);
    }
  }

  const handleGoalChange = async (newGoal: Partial<SleepGoal>) => {
    if (!user || !selectedChildId) return;

    try {
      if (selectedChildGoal) {
        const response = await apiClient.updateGoal({
          ...selectedChildGoal,
          ...newGoal
        });
        if (response.success && response.data) {
          trackEvent(AnalyticsEvents.GOAL_UPDATED);
          // Refresh goals
          const goalsResponse = await apiClient.getGoals(selectedChildId);
          if (goalsResponse.success && goalsResponse.data) {
            setGoals(goalsResponse.data);
            setCachedGoals(user.uid, selectedChildId, goalsResponse.data);
          }
        }
      } else {
        const response = await apiClient.createGoal({
          ...(newGoal as any),
          childId: selectedChildId
        });
        if (response.success && response.data) {
          trackEvent(AnalyticsEvents.GOAL_CREATED);

          // Refresh goals
          const goalsResponse = await apiClient.getGoals(selectedChildId);
          if (goalsResponse.success && goalsResponse.data) {
            setGoals(goalsResponse.data);
            setCachedGoals(user.uid, selectedChildId, goalsResponse.data);
          }
        }
      }
    } catch (error) {
      console.error("Error updating goal:", error);
    }
  }

  const handleProfileSave = async (newChild: Omit<Child, 'id' | 'userId'> | Omit<Child, 'userId'>) => {
    if (!user) return;

    // Check child limit for free users when creating new child
    if (!isSubscribed && !('id' in newChild) && children.length >= MAX_FREE_CHILDREN) {
      toast({
        variant: "destructive",
        title: "Child Limit Reached",
        description: `Free accounts can have up to ${MAX_FREE_CHILDREN} children. Subscribe to add more.`,
      });
      return;
    }

    try {
      if ('id' in newChild && newChild.id) {
        // Update existing child
        const response = await apiClient.updateChild(newChild as Child);
        if (response.success && response.data) {
          // Track child profile edit
          trackEvent(AnalyticsEvents.CHILD_PROFILE_EDITED);

          // Refresh children
          const childrenResponse = await apiClient.getChildren();
          if (childrenResponse.success && childrenResponse.data) {
            setChildren(childrenResponse.data);
            setCachedChildren(user.uid, childrenResponse.data);
          }
        }
      } else {
        // Create new child
        const response = await apiClient.createChild(newChild as Omit<Child, 'id' | 'userId'>);
        if (response.success && response.data) {
          setSelectedChildId(response.data.id);
          // Track child profile creation
          trackEvent(AnalyticsEvents.CHILD_PROFILE_CREATED, { child_gender: (newChild as any).gender || 'unknown' });

          // Track onboarding complete if this is the first child
          if (children.length === 0) {
            trackEvent(AnalyticsEvents.ONBOARDING_COMPLETE, { child_gender: (newChild as any).gender || 'unknown' });
          }

          // Refresh children
          const childrenResponse = await apiClient.getChildren();
          if (childrenResponse.success && childrenResponse.data) {
            setChildren(childrenResponse.data);
            setCachedChildren(user.uid, childrenResponse.data);
          }
        }
      }
    } catch (error) {
      console.error("Error saving child profile:", error);
    }
  }



  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            <p className="text-sm text-muted-foreground">Loading family data...</p>
          </div>
        </div>
      );
    }

    if (user && children.length === 0) {
      return <OnboardingWelcome onSave={handleProfileSave} />
    }

    switch (activeTab) {
      case 'home':
        return <HomeTab events={events} child={selectedChild} goal={selectedChildGoal} />;
      case 'log':
        return <LogTab
          events={events}
          childId={selectedChildId || undefined}
          onEditEvent={updateSleepEvent}
          onDeleteEvent={deleteSleepEvent}
          onAddEvent={addSleepEvent}
          onAddEventsBatch={addSleepEventsBatch}
          goal={selectedChildGoal}
        />;
      case 'goals':
        return selectedChild ? <GoalsTab child={selectedChild} goal={selectedChildGoal} onGoalChange={handleGoalChange} events={events} isSubscribed={isSubscribed} /> : null;
      case 'coach':
        return <AICoachTab events={events} child={selectedChild} goal={selectedChildGoal} onGoalChange={handleGoalChange} />;
      default:
        return <HomeTab events={events} child={selectedChild} goal={selectedChildGoal} />;
    }
  };

  return (
    <AuthGuard>
      <div className="flex min-h-screen w-full flex-col bg-background safe-area-left safe-area-right overflow-x-clip min-w-0">
        <div className="sticky top-0 z-20 bg-card border-b shadow-xs">
          <Header className={children.length > 1 ? "static border-b-0 bg-card" : "static bg-card"} />
          {children.length > 1 && (
            <ChildSelection
              children={children}
              selectedChildId={selectedChildId}
              onSelectChild={setSelectedChildId}
              onProfileSave={handleProfileSave}
            />
          )}
        </div>

        <main className="flex flex-1 flex-col pb-[calc(6.5rem+env(safe-area-inset-bottom))] max-w-full overflow-x-clip min-w-0">
          {renderContent()}
        </main>

        {selectedChildId && (
          <>
            <SleepLogDialog
              open={isSleepLogOpen}
              onOpenChange={setIsSleepLogOpen}
              onSave={addSleepEvent}
              onSaveBatch={addSleepEventsBatch}
              onEdit={updateSleepEvent}
              disabled={!selectedChildId}
              goal={selectedChildGoal}
            />
            <TabsLayout
              activeTab={activeTab}
              onTabChange={(tab) => {
                if (tab !== 'coach') {
                  scrollToTop();
                }
                setActiveTab(tab);
                trackEvent(AnalyticsEvents.TAB_CHANGED, { tab_name: tab });
                // Map tab ID to a readable screen name
                const screenNames: Record<string, string> = { home: 'Home', log: 'Sleep Log', goals: 'Goals', coach: 'AI Coach' };
                trackScreenView(screenNames[tab] || tab);
              }}
              onAddClick={() => setIsSleepLogOpen(true)}
            />
          </>
        )}
      </div>
    </AuthGuard>
  );
}
